import { BehaviorSubject, fromEvent, map, type Observable, startWith } from 'rxjs';
import type { Locale } from 'date-fns';
import {
    formatDuration,
    intervalToDuration,
    secondsToMilliseconds,
    setYear,
    startOfYear,
} from 'date-fns';
import de from 'date-fns/locale/de/index.js';
import enGB from 'date-fns/locale/en-GB/index.js';
import fr from 'date-fns/locale/fr/index.js';
import nl from 'date-fns/locale/nl/index.js';
import { useObservableEagerState } from 'observable-hooks';
import i18n from '../translations/i18n';

const localeMap: Record<Language, Locale> = {
    de,
    en: enGB,
    fr,
    nl,
};

function getLocale(language: Language): Locale {
    const locale = localeMap[language];

    if (!locale) {
        throw new Error(
            `Unsupported date-fns locale "${language}". Add it to localeMap in assets/js/utils/date-fns.ts.`
        );
    }

    return locale;
}

export const locale$ = new BehaviorSubject(getLocale(i18n.language as Language));

export const language$ = fromEvent(i18n, 'languageChanged').pipe(
    startWith(i18n.language)
) as Observable<Language>;

// Listen for updates on language$.
language$
    .pipe(
        // Map those language changes to an locale.
        map((language) => getLocale(language))
    )
    // Push updates to `locale$`.
    .subscribe(locale$);

export function useLocale() {
    return useObservableEagerState(locale$);
}

export function getWeekDayWide(
    locale: Locale,
    weekDay: number,
    width: 'narrow' | 'short' | 'long' | 'wide' = 'wide'
) {
    return String(locale.localize?.day(weekDay, { width }));
}

export const startOfOmnidots = startOfYear(setYear(new Date(), 2017));

export function secondsToDurationText(seconds: number, locale: Locale): string {
    const duration = intervalToDuration({ start: 0, end: secondsToMilliseconds(seconds) });
    return formatDuration(duration, { locale });
}

export default getLocale;
